import { Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { LazyLoader } from "@/components/ui/LazyLoader";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";

// Import lazy components from our optimized lazy imports
import {
  LazyIndex,
  LazyAuth,
  LazyWallet,
  LazyTransactions,
  LazyQRCodes,
  LazyQREdit,
  LazyProfile,
  LazyContacts,
  LazyQuickTip,
  LazyAnalytics,
  LazyAccount,
  LazyNearbyUsers,
  LazyZtartDDF
} from "@/utils/lazyImports";

// Lazy load remaining components
const PersonalInfo = lazy(() => import("@/pages/PersonalInfo"));
const Businesses = lazy(() => import("@/pages/Businesses"));
const Payments = lazy(() => import("@/pages/Payments"));
const PaymentMethods = lazy(() => import("@/pages/PaymentMethods"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const Budget = lazy(() => import("@/pages/Budget"));
const Goals = lazy(() => import("@/pages/Goals"));
const Investments = lazy(() => import("@/pages/Investments"));
const Activity = lazy(() => import("@/pages/Activity"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const SkinerCoherenceDemo = lazy(() => import("@/pages/SkinerCoherenceDemo"));
const ConversationalAgentDemo = lazy(() => import("@/pages/ConversationalAgentDemo"));
const Observatory = lazy(() => import("@/pages/Observatory"));
const MetaDashboardExecutive = lazy(() => import("@/pages/MetaDashboardExecutive"));
const TipsMe = lazy(() => import("@/pages/TipsMe"));
const Admin = lazy(() => import("@/pages/Admin"));
const BusinessAdminDashboard = lazy(() => import("@/pages/business-admin/BusinessAdminDashboard"));
const BusinessTeamPage = lazy(() => import("@/pages/business-admin/BusinessTeamPage"));

// Componente de loading optimizado con mejor UX
const PageLoadingFallback = () => (
  <div className="min-h-screen flex flex-col bg-background">
    <div className="h-16 bg-muted/50 animate-pulse border-b" />
    <div className="flex-1 p-4 space-y-6">
      {/* Header skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>

      {/* Main content skeleton */}
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>

      {/* Additional content skeleton */}
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/4" />
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    </div>
  </div>
);

// Optimized loading component for heavy pages
const HeavyPageLoadingFallback = () => (
  <div className="min-h-screen flex flex-col bg-background">
    <div className="h-16 bg-muted/50 animate-pulse border-b" />
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground">Cargando contenido...</p>
      </div>
    </div>
  </div>
);

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Main routes with optimized loading */}
      <Route path="/" element={
        <LazyLoader fallback={<PageLoadingFallback />}>
          <LazyIndex />
        </LazyLoader>
      } />

      <Route path="/auth" element={
        <LazyLoader fallback={<PageLoadingFallback />}>
          <LazyAuth />
        </LazyLoader>
      } />

      <Route path="/wallet" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <LazyWallet />
        </LazyLoader>
      } />

      <Route path="/transactions" element={
        <LazyLoader fallback={<PageLoadingFallback />}>
          <LazyTransactions />
        </LazyLoader>
      } />

      <Route path="/qr-codes" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <LazyQRCodes />
        </LazyLoader>
      } />

      <Route path="/qr-codes/:id/edit" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <LazyQREdit />
        </LazyLoader>
      } />

      <Route path="/profile" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <LazyProfile />
        </LazyLoader>
      } />

      <Route path="/profile/edit" element={
        <Navigate to="/account" state={{ defaultTab: 'profile' }} replace />
      } />

      <Route path="/businesses" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <Businesses />
        </Suspense>
      } />

      <Route path="/contacts" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <LazyContacts />
        </LazyLoader>
      } />

      <Route path="/nearby-users" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <LazyNearbyUsers />
        </LazyLoader>
      } />

      <Route path="/quick-tip" element={
        <LazyLoader fallback={<PageLoadingFallback />}>
          <LazyQuickTip />
        </LazyLoader>
      } />

      <Route path="/tips-me" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <TipsMe />
        </Suspense>
      } />

      <Route path="/settings" element={
        <Navigate to="/account" state={{ defaultTab: 'security' }} replace />
      } />

      <Route path="/payments" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <Payments />
        </Suspense>
      } />

      <Route path="/payment-methods" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <PaymentMethods />
        </Suspense>
      } />

      <Route path="/invoices" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <Invoices />
        </Suspense>
      } />

      <Route path="/budget" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <Budget />
        </Suspense>
      } />

      <Route path="/goals" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <Goals />
        </Suspense>
      } />

      <Route path="/investments" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <Investments />
        </Suspense>
      } />

      <Route path="/analytics" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <LazyAnalytics />
        </LazyLoader>
      } />

      <Route path="/activity" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <Activity />
        </Suspense>
      } />

      <Route path="/account" element={
        <LazyLoader fallback={<PageLoadingFallback />}>
          <LazyAccount />
        </LazyLoader>
      } />

      <Route path="/notifications" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <Notifications />
        </Suspense>
      } />

      <Route path="/ztart-ddf" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <LazyZtartDDF />
        </LazyLoader>
      } />

      <Route path="/skiner-coherence" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <SkinerCoherenceDemo />
        </LazyLoader>
      } />

      <Route path="/conversational-agent" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <ConversationalAgentDemo />
        </LazyLoader>
      } />

      <Route path="/observatory" element={
        <LazyLoader fallback={<HeavyPageLoadingFallback />}>
          <Observatory />
        </LazyLoader>
      } />

      <Route path="/meta-dashboard" element={
        <ProtectedRoute feature="metaDashboard">
          <LazyLoader fallback={<HeavyPageLoadingFallback />}>
            <MetaDashboardExecutive />
          </LazyLoader>
        </ProtectedRoute>
      } />

      <Route path="/admin" element={
        <ProtectedRoute feature="admin">
          <LazyLoader fallback={<HeavyPageLoadingFallback />}>
            <Admin />
          </LazyLoader>
        </ProtectedRoute>
      } />

      <Route path="/business-admin" element={
        <Suspense fallback={<HeavyPageLoadingFallback />}>
          <BusinessAdminDashboard />
        </Suspense>
      } />

      <Route path="/business-admin/team" element={
        <Suspense fallback={<HeavyPageLoadingFallback />}>
          <BusinessTeamPage />
        </Suspense>
      } />

      <Route path="*" element={
        <Suspense fallback={<PageLoadingFallback />}>
          <NotFound />
        </Suspense>
      } />
    </Routes>
  );
};

export default AppRoutes;
